<template>
  <div class="leaderboard-table" :id="`leaderboard-table-${leaderboard.id}`" v-if="Array.isArray(tableItems)">
    <b-table hover :fields="tableFields" :items="tableItems" primary-key="entryID"
      :tbody-transition-props="animate ? { name: 'flip-list' } : null">

        <template #cell(position)="{ index, item: { isDivider, position, total } }">
          <template v-if="leaderboard.raffleMode">
            <template v-if="total > 0">🎟</template>
          </template>
          <template v-else>
            <img src="/static/images/icons/medals/gold-medal.svg" :alt="$t('leaderboards.prizes.first-place')" v-if="total > 0 && position === 1"/>
            <img src="/static/images/icons/medals/silver-medal.svg" :alt="$t('leaderboards.prizes.second-place')" v-else-if="total > 0 && position === 2"/>
            <img src="/static/images/icons/medals/bronze-medal.svg" :alt="$t('leaderboards.prizes.third-place')" v-else-if="total > 0 && position === 3"/>
            <template v-else-if="position">{{position}}</template>
          </template>
        </template>

        <template #cell(avatar)="{ item: { isDivider, entry } }">
          <font-awesome-icon v-if="!isDivider && entry && entry.icon" class="rounded-circle primary-color" :icon="entry.icon"/>
          <avatar v-else-if="!isDivider" :user="entry" :size="40"/>
        </template>
        <template #cell(name)="{ item: { isDivider, entryID, entry } }">
          <template v-if="isDivider">...</template>
          <template v-else-if="entry && entry.id">{{ entry.fullName || entry.name || entry.title }}</template>
          <template v-else>{{ entryID }}</template>
        </template>

    </b-table>
    <div class="d-flex flex-row justify-content-center align-items-center" v-if="typeof limit === 'number' && limit > 0">
      <b-pagination v-model="page" :per-page="limit" :total-rows="edges.length"/>
    </div>
  </div>
</template>

<script>
import Avatar from '@src/components/users/Avatar';
import { createMD5String } from '@src/lib/hashing';
import { mapRoot } from '@src/mixins/root';

export default {
  name: 'LeaderboardTable',
  props: {
    leaderboard: {
      type: Object,
      required: true,
    },
    animate: {
      type: Boolean,
      default: () => false,
    },
    fields: Array,
    limit: Number,
  },
  components: {
    Avatar,
  },
  data: () => ({
    page: 1,
  }),
  computed: {
    ...mapRoot([ 'currentUserID', 'isRecruiterAdmin' ]),
    isDepartmentLeaderboard() {
      return this.leaderboard && this.leaderboard.type === 'DEPARTMENTS';
    },
    edges() {
      if (this.leaderboard && Array.isArray(this.leaderboard.segments) && this.leaderboard.segments.length) {
        const { segments, userScores } = this.leaderboard;
        const edges = ((userScores && Array.isArray(userScores.edges)) ? userScores.edges : []).reduce((list, edge, i) => {
          if (edge && edge.entry && edge.entry.id) {
            const { id: entryID } = edge.entry;
            list.push({ entryID, position: i + 1, ...edge });
          }
          return list;
        }, []);

        if (this.leaderboard.groupBy === 'SEGMENTS') {
          return segments.map(segment => {
            const { id, usersConnection } = segment;
            const segmentEdge = {
              entryID: id,
              entry: segment,
              avatar: segment.icon ? null : `/gravatar/${createMD5String(id)}`,
              scores: [],
            };

            if (usersConnection && Array.isArray(usersConnection.edges)) {
              const userIDs = usersConnection.edges.map(({ id }) => id);
              segmentEdge.scores = edges.reduce((list, userEdge) => {
                const { entryID, scores } = userEdge;
                return scores && userIDs.includes(entryID) ? list.concat(scores) : list;
              }, []).map(score => {
                score.value = parseInt(score.value, 10);
                return score;
              });
            }
            segmentEdge.total = segmentEdge.scores.reduce((sum, { value }) => sum + value, 0);
            return segmentEdge;
          }).sort(({ total: aTotal }, { total: bTotal }) => {
            if (aTotal < bTotal) {
              return 1;
            } else if (aTotal > bTotal) {
              return -1;
            } else {
              return 0;
            }
          });
        } else {
          return edges;
        }
      } else {
        const scores = this.isDepartmentLeaderboard ? this.leaderboard.departmentScores : this.leaderboard.userScores;
        return (scores && Array.isArray(scores.edges) ? scores.edges : []).reduce((list, edge, i) => {
          if (edge && edge.entry && edge.entry.id) {
            const { id: entryID } = edge.entry;
            list.push({ entryID, position: i + 1, ...edge });
          }
          return list;
        }, []);
      }
    },
    includesCurrentUser() {
      return this.edges.findIndex(({ entryID }) => entryID === this.currentUserID) >= 0;
    },
    tableFields() {
      const { raffleMode, scores } = this.leaderboard || {};

      let fields = [
        {
          key: 'position',
          label: raffleMode ? '🎟' : '🏆',
          thStyle: { width: '50px' },
        },
        {
          key: 'avatar',
          label: this.$t('users.labels.avatar'),
          thStyle: { width: '60px' },
        },
        {
          key: 'name',
          label: this.$t('users.labels.name'),
          thStyle: { width: '300px' },
        },
      ];

      (Array.isArray(scores) ? scores : []).filter(({ disabled }) => !disabled).forEach(score => {
        fields.push({
          key: score.id,
          label: score.label,
          thStyle: { width: '120px' },
          formatter(v, k, edge) {
            if (edge.isDivider) {
              return null;
            } else {
              const matches = (Array.isArray(edge.scores) ? edge.scores : []).filter(s => score.id === s.key);
              return matches.reduce((sum, { value }) => sum + parseInt(value, 10), 0);
            }
          },
        });
      });

      fields.push({
        key: 'total',
        label: this.$t(raffleMode ? 'leaderboards.labels.total-tickets' : 'leaderboards.labels.total-score'),
        thStyle: { width: '120px' },
        formatter: (value, i, edge) => value || (edge.isDivider ? null : 0),
      });

      if (Array.isArray(this.fields)) {
        fields = fields.filter(({ key }) => this.fields.includes(key));
      }

      return fields;
    },
    tableItems() {
      if (Array.isArray(this.edges) && this.edges.length) {
        const offset = Math.max(0, this.page - 1) * this.limit;
        const items = this.limit ? this.edges.slice(offset, offset + this.limit) : this.edges;

        if (this.limit && this.includesCurrentUser && !items.find(({ entryID }) => this.currentUserID === entryID)) {
          const i = this.edges.findIndex(({ entryID }) => entryID === this.currentUserID);
          if (i > offset) {
            items.push({ entryID: 'DIVIDER', isDivider: true }, this.edges[i]);
          }
        }

        return items;
      } else {
        return null;
      }
    },
  },
  watch: {
    page() {
      this.$scrollTo(`#leaderboard-table-${this.leaderboard.id}`);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/leaderboards/Table.vue